/**
 * Upload Service
 * Handles file uploads to S3 via signed URLs
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Generate signed URLs for file uploads
 * @param {array} files - Array of file objects with name, type
 * @param {boolean} isPrivate - Whether files should be private
 * @returns {Promise<object>} - Response with signed URLs
 */
export const generateSignedURLs = async (files, isPrivate = false) => {
  try {
    const requestData = {
      files: files.map(file => {
        const fileData = {
          name: file.name,
          type: file.type,
        };

        if (isPrivate) {
          fileData.private = true;
        }

        return fileData;
      }),
    };

    // Use the path directly to avoid any potential constant access issues
    const uploadPath = 'v1/utils/S3signedUploadURL';

    const response = await network.networkCall(
      uploadPath,
      'POST',
      JSON.stringify(requestData),
    );

    return response;
  } catch (error) {
    console.error('Error generating signed URLs:', error);
    throw error;
  }
};

/**
 * Upload file to S3 using signed URL
 * @param {string} signedUrl - Signed URL from S3
 * @param {string} mimeType - File MIME type
 * @param {Blob|File} fileBody - File content
 * @returns {Promise<boolean>} - Success status
 */
export const uploadToS3 = async (signedUrl, mimeType, fileBody, onProgress) => {
  try {
    return await new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', signedUrl);
      request.setRequestHeader('Content-Type', mimeType || 'application/octet-stream');
      request.upload.onprogress = event => {
        if (event.lengthComputable) onProgress?.(event.loaded / event.total);
      };
      request.onload = () => resolve(request.status >= 200 && request.status < 300);
      request.onerror = () => reject(new Error('Network error while uploading file'));
      request.onabort = () => reject(new Error('File upload was cancelled'));
      request.send(fileBody);
    });
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
};

/**
 * Upload multiple images to S3
 * @param {array} files - Array of file paths or file objects
 * @param {boolean} isPrivate - Whether files should be private
 * @returns {Promise<string[]>} - Array of uploaded file URLs
 */
export const uploadImages = async (files, isPrivate = false, onProgress) => {
  try {
    const uploadResults = [];
    const filesToUpload = [];
    const uploadIndexes = [];

    // Separate existing URLs from new uploads
    files.forEach((file, index) => {
      if (isRemoteUrl(file)) {
        // Already uploaded, use as-is
        uploadResults[index] = file;
      } else if (file && file.full_file) {
        // New upload needed
        const rawName = file.name || generateFileName(file.full_file);
        const fileData = {
          name: rawName.replace(/\s/g, '-'),
          type: file.type || getFileMimeType(rawName),
          full_file: file.full_file,
        };
        filesToUpload.push(fileData);
        uploadIndexes.push(index);
      } else if (typeof file === 'string') {
        // Local file path
        const fileData = {
          name: generateFileName(file),
          type: getFileMimeType(file),
          path: file,
        };
        filesToUpload.push(fileData);
        uploadIndexes.push(index);
      } else {
        console.warn(
          `SKIPPED - Unhandled format:`,
          typeof file,
          file,
        );
      }
    });

    if (filesToUpload.length === 0) {
      return files; // All files already uploaded
    }

    // Generate signed URLs
    const signedResponse = await generateSignedURLs(filesToUpload, isPrivate);

    if (!signedResponse.status || !signedResponse.data?.result) {
      console.error('Invalid signed response:', signedResponse);
      throw new Error('Failed to generate signed URLs');
    }

    const signedUrls = signedResponse.data.result;

    // Upload each file to S3
    const completedProgress = new Array(signedUrls.length).fill(0);
    const reportProgress = index => {
      if (!onProgress) return;
      const overall = completedProgress.reduce((total, value) => total + value, 0);
      onProgress(Math.round((overall / signedUrls.length) * 100));
      completedProgress[index] = 1;
    };

    for (let i = 0; i < signedUrls.length; i++) {
      const {signedUrl, fileUri} = signedUrls[i];
      const fileData = filesToUpload[i];

      let fileBody;
      if (fileData.full_file instanceof Blob) {
        fileBody = fileData.full_file;
      } else if (fileData.path) {
        // For React Native, you might need to use FileSystem or similar
        fileBody = await readFileAsBlob(fileData.path);
      }

      const success = await uploadToS3(signedUrl, fileData.type, fileBody, progress => {
        completedProgress[i] = progress;
        onProgress?.(Math.round((completedProgress.reduce((total, value) => total + value, 0) / signedUrls.length) * 100));
      });

      if (success) {
        uploadResults[uploadIndexes[i]] = fileUri;
        reportProgress(i);
      } else {
        throw new Error(`Failed to upload file ${i + 1}`);
      }
    }

    // Return in original order
    return uploadResults;
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
};

/**
 * Upload a single file to S3
 * @param {string|object} file - File path or file object
 * @param {boolean} isPrivate - Whether file should be private
 * @returns {Promise<string>} - Uploaded file URL
 */
export const uploadSingleFile = async (file, isPrivate = false) => {
  const results = await uploadImages([file], isPrivate);
  return results[0];
};

/**
 * Check if URL is a remote URL (already uploaded)
 * @param {string} uri - URI to check
 * @returns {boolean}
 */
export const isRemoteUrl = uri => {
  if (!uri) return false;

  const remotePatterns = [
    /^https?:\/\//, // http:// or https://
    /^ftp:\/\//, // ftp://
    /^s3:\/\//, // s3://
    /^\/\//, // protocol-relative URL
  ];

  return remotePatterns.some(pattern => pattern.test(uri));
};

/**
 * Get file MIME type from filename
 * @param {string} filename - File name
 * @returns {string} - MIME type
 */
export const getFileMimeType = filename => {
  const ext = filename.split('.').pop().toLowerCase();

  const mimeTypes = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    webm: 'video/webm',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    pdf: 'application/pdf',
    csv: 'text/csv',
    json: 'application/json',
    zip: 'application/zip',
  };

  return mimeTypes[ext] || 'application/octet-stream';
};

/**
 * Generate a filename for upload
 * @param {string} filepath - File path or name
 * @returns {string} - Generated filename
 */
export const generateFileName = filepath => {
  const parts = filepath.split('/');
  const name = parts[parts.length - 1];
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
};

/**
 * Read file as blob (for React Native file paths)
 * @param {string} filepath - File path
 * @returns {Promise<Blob>}
 */
export const readFileAsBlob = async filepath => {
  // This is a placeholder implementation
  // In React Native, you'd use FileSystem or similar
  // For web, this would work with fetch
  try {
    const response = await fetch(filepath);
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Error reading file as blob:', error);
    throw error;
  }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size
 */
export const formatFileSize = bytes => {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
